import { Button } from '@creditornot/cb-button';
import { Trashbin } from '@creditornot/cb-icons';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cb-modal';
import { useMutation } from '@tanstack/react-query';
import React, { useCallback } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { APIErrorNotification, WoltModal } from 'common/components';
import { Typography } from 'common/typography';
import { localize } from 'common/utils';
import { sharedMenuInteractionClickDeleteMenu } from 'core/telemetry/avo/Avo';
import { deleteSharedMenu } from 'modules/shared-menu/api';
import { CustomToast } from 'modules/shared-menu/components/CustomToast';
import { useScopeContext } from 'modules/shared-menu/contexts/ScopeContext';
import { useListingManagerRoutes } from 'modules/shared-menu/hooks/useListingManagerRoutes';
import { useSharedMenuData } from 'modules/shared-menu/hooks/useSharedMenuData';
import { SharedMenu } from 'modules/shared-menu/types';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  sharedMenu: SharedMenu;
};

const DeleteModal = styled(WoltModal)`
  min-width: 480px;
`;

const ModalButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  width: 100%;
`;

const DELETE_SHARED_MENU_TOAST_ID = 'DELETE_SHARED_MENU_TOAST_ID';

export const DeleteSharedMenuModal = ({ isOpen, onClose, sharedMenu }: Props) => {
  const scope = useScopeContext();
  const history = useHistory();
  const { rootPage } = useListingManagerRoutes();
  const { formatMessage } = useIntl();
  const { refetchSharedMenus } = useSharedMenuData({ scope });

  const { mutate, isLoading, error, reset } = useMutation(() => deleteSharedMenu(sharedMenu.id), {
    onSuccess: async () => {
      sharedMenuInteractionClickDeleteMenu({ sharedMenuId: sharedMenu.id });
      await refetchSharedMenus();
      history.push(rootPage());
      onClose();
      toast.custom(
        <CustomToast
          variant="success"
          title={formatMessage({ id: 'shared-menu.editor.delete.toasts.success.title' })}
          message={formatMessage(
            { id: 'shared-menu.editor.delete.toasts.success.message' },
            { menuName: localize(sharedMenu.name, sharedMenu.primary_language) },
          )}
          toastId={DELETE_SHARED_MENU_TOAST_ID}
        />,
        { id: DELETE_SHARED_MENU_TOAST_ID },
      );
    },
    onError: () => {
      toast.custom(
        <CustomToast
          variant="error"
          title={formatMessage({ id: 'shared-menu.editor.delete.toasts.error.title' })}
          message={formatMessage({ id: 'shared-menu.editor.delete.toasts.error.message' })}
          toastId={DELETE_SHARED_MENU_TOAST_ID}
        />,
        { id: DELETE_SHARED_MENU_TOAST_ID, duration: Infinity },
      );
    },
  });

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  return (
    <DeleteModal isOpen={isOpen} onRequestClose={handleClose}>
      <ModalHeader onCloseClick={handleClose}>
        <FormattedMessage id="shared-menu.editor.delete.modal.title.confirm" />
      </ModalHeader>
      <ModalContent>
        <Typography.Body3>
          <FormattedMessage
            id="shared-menu.editor.delete.modal.description.confirm"
            values={{
              menuName: <b>{localize(sharedMenu.name, sharedMenu.primary_language)}</b>,
            }}
          />
        </Typography.Body3>
      </ModalContent>
      <ModalFooter>
        {error && <APIErrorNotification error={error} style={{ width: '100%' }} />}
        <ModalButtons>
          <Button size="small" onClick={handleClose} variant="lightBlue">
            <FormattedMessage id="state.cancel" />
          </Button>
          <Button size="small" icon={<Trashbin />} onClick={() => mutate()} loading={isLoading} variant="lightRed">
            <FormattedMessage id="shared-menu.editor.delete.modal.button.confirm" />
          </Button>
        </ModalButtons>
      </ModalFooter>
    </DeleteModal>
  );
};
